import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components/macro";
import { NavLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import SyncIcon from "@mui/icons-material/Sync";
import validator from "validator";
import SearchIcon from '@mui/icons-material/Search';

import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Paper as MuiPaper,
  TextField,
  Typography,
  Button,
  Snackbar,
  Alert,
  Autocomplete,
  FormControl,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  IconButton,
  Collapse,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { spacing } from "@mui/system";
import axios from "../../CustomAxios";
import { Formik } from "formik";
import Loading from "../components/Loading";
import { connect } from "react-redux";
import { DateTimePicker, LoadingButton } from "@mui/lab";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const Card = styled(MuiCard)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function createData(id, challan_id, name, description, data) {
  return { id, challan_id, name, description, data };
}

function FormDetails({ details, id, authenticatedUser, challanList, setChallanList }) {
  const userData = JSON.parse(localStorage.getItem("AuthData"));
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [loader, setLoader] = useState(false);
  const snackRef = useRef(null);
  const [state, setState] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [billSyncing, setBillSyncing] = useState(false);
  const [searchChallan, setSearchChallan] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState(findSelectedAddress());
  const [showNtb, setShowNtb] = useState(details?.show_not_to_bill === false ? false : true);
  const [roundOff, setRoundOff] = useState(details?.round_off === false ? false : true);
  const [down, setDown] = useState({});
  const [allChallanTableRows] = useState(setAllChalanRows());
  const [challanTableRows, setChallanTableRows] = useState([]);
  const [price, setPrice] = useState(details ? details.total_bill_cost_details.total_taxable_amount : 0);
  const [selectedChallans, setSelectedChallans] = useState(setupSellectedChallanList());
  const [currentBillingDate, setCurrentBillingDate] = useState(new Date(details?.billing_date));
  const [challanPrices, setChallanPrices] = useState({});
  const [currentSelectedChallans] = useState(setupCurrentSelectedChallanList());
  const [lastDate, setLastDate] = useState();
  const [discount_percent, setDiscountPercent] = useState(details?.discount_percent || 0);
  const [transport_charges, setTransportCharges] = useState(details?.transport_charges || 0);
  const [hsnCode, setHsnCode] = useState(details?.hsn_code || '');
  const cash_payment = details?.cash_payment || false;  // read from bill details

  const initialDiscountPercent = details?.discount_percent || 0;
  const initialTransportCharges = details?.transport_charges || 0;
  let originalPrice;

  if (details.discount_percent && details.transport_charges) {
    originalPrice = (details.total_bill_cost_details.total_taxable_amount - details.transport_charges) * 100 / (100 - details.discount_percent);
  } else if (details.discount_percent && !details.transport_charges) {
    originalPrice = details.total_bill_cost_details.total_taxable_amount * 100 / (100 - details.discount_percent);
  } else if (!details.discount_percent && details.transport_charges) {
    originalPrice = details.total_bill_cost_details.total_taxable_amount - details.transport_charges;
  } else {
    originalPrice = details.total_bill_cost_details.total_taxable_amount;
  }

  const { vertical, horizontal, open } = state;
  const handleClick = (newState) => () => {
    setState({ open: true, ...newState });
  };
  const handleClose = () => {
    setState({ ...state, open: false });
  };

  const selectedCount = () => Object.values(selectedChallans).filter(v => v === true).length;

  const submit = async (values, id) => {
    try {
      let challans = [];
      Object.keys(selectedChallans).forEach((challan) => {
        if (selectedChallans[challan]) {
          challans.push(challan);
        }
      });
      if (!challans.length) {
        setErr("Select at least one Challan");
        snackRef.current.click();
        return;
      }
      if (challans.length > 10) {
        setErr("Cannot select more than 10 challans");
        snackRef.current.click();
        return;
      }
      if (!deliveryAddress || (deliveryAddress && !validator.isMongoId(deliveryAddress._id || ""))) {
        setErr("Please select the delivery address");
        snackRef.current.click();
        return;
      }

      setLoader(true);
      const config = {
        headers: {
          Authorization: "Bearer " + userData.access_token,
        },
      };
      const { data } = await axios.put(
        `/api/v1/${userData.type}/bill/${id}`,
        {
          name: values.name,
          description: values.description,
          delivery: deliveryAddress,
          billing_date: new Date(values.billing_date).toISOString(),
          challans: challans,
          show_ntb: showNtb,
          round_off: roundOff,
          discount_percent: parseInt(discount_percent, 10) || 0,
          transport_charges: parseInt(transport_charges, 10) || 0,
          hsn_code: hsnCode,
        },
        config
      );
      if (data.success) {
        setLoader(false);
        setSuccess(true);
        setErr("");
        snackRef.current.click();
        setSuccessMessage("Bill Updated Successfully");
      }
    } catch (e) {
      setLoader(false);
      if (e.response.data.error) {
        setErr(e.response.data.error);
      } else {
        setErr("Server Error");
      }
      snackRef.current.click();
    }
  };

  const getLastBillDate = async (ap_company_id) => {
    try {
      const userData = JSON.parse(localStorage.getItem("AuthData"));
      const config = {
        headers: {
          Authorization: "Bearer " + userData.access_token,
        },
      };
      const { data } = await axios.get(
        `/api/v1/${userData.type}/bill/last/date/${ap_company_id}`,
        config
      );
      if (data.success) {
        setLastDate(data.data);
      }
    } catch (e) {
      setLoader(false);
      if (e.response.data.error) {
        setErr(e.response.data.error);
      } else {
        setErr("Server Error");
        snackRef.current.click();
      }
    }
  };

  async function handleBillSync() {
    setBillSyncing(true);
    try {
      setLoader(true);
      const userData = JSON.parse(localStorage.getItem("AuthData"));
      const config = {
        headers: {
          Authorization: "Bearer " + userData.access_token,
        },
      };
      const { data } = await axios.put(`/api/v1/${userData.type}/bill/${id}/sync`, {}, config);
      if (data.success) {
        setLoader(false);
        setSuccess(true);
        setErr("");
        snackRef.current.click();
        setSuccessMessage("Job Synced Successfully");
      }
    } catch (e) {
      setErr(e.response.data.error);
      snackRef.current.click();
      setLoader(false);
      console.log(e);
    }
    setBillSyncing(false);
  }

  function findSelectedAddress() {
    const { address_line_one, address_line_two, city, pincode, state } = details?.delivery;
    return (
      details?.customer_company?.addresses?.find(
        (k) =>
          isStringEqual(k.address_line_one, address_line_one) &&
          isStringEqual(k.address_line_two, address_line_two) &&
          isStringEqual(k.city, city) &&
          isStringEqual(k.pincode, pincode) &&
          isStringEqual(k.state, state)
      ) || ""
    );
  }

  function setupChallanTableRows() {
    let challans = [...details?.challans, ...challanList];
    const tempChallans = challans.filter((challan) => challan.challan_id.toLowerCase().startsWith(searchChallan.toLowerCase()));
    let rows = [];
    tempChallans.forEach((row) => {
      rows.push(createData(row._id, row.challan_id, row.name, row.description, row));
    });
    setChallanTableRows(rows);
  }

  function setAllChalanRows() {
    let rows = [];
    details?.challans.forEach((row) => {
      rows.push(createData(row._id, row.challan_id, row.name, row.description, row));
    });
    challanList.forEach((row) => {
      rows.push(createData(row._id, row.challan_id, row.name, row.description, row));
    });
    return rows;
  }

  function setupSellectedChallanList() {
    const selectedObjects = {};
    details?.challans.forEach((row) => {
      selectedObjects[row._id] = true;
    });
    return selectedObjects;
  }

  function setupCurrentSelectedChallanList() {
    const rowList = [];
    details?.challans.forEach((row) => {
      rowList.push(row._id);
    });
    return rowList;
  }

  function isStringEqual(str1, str2) {
    return str1.toLowerCase() === str2.toLowerCase();
  }

  function processDateFormat(dateInput) {
    var month = ("0" + parseInt(dateInput?.getMonth() + 1)).slice(-2);
    var day = ("0" + parseInt(dateInput?.getDate())).slice(-2);
    var hour = parseInt(dateInput.getHours());
    var minute = parseInt(dateInput.getMinutes());
    return `${dateInput?.getFullYear()}-${month}-${day}:${hour}:${minute}`;
  }

  function showErrorAlert(message) {
    setErr(message);
    snackRef.current.click();
  }

  function checkSelectedDateIsInBetween(selectedDateInput) {
    const challanDates = [];
    Object.keys(selectedChallans).forEach((challan) => {
      if (selectedChallans[challan]) {
        const currentChallan = allChallanTableRows.find((it) => it.id === challan);
        challanDates.push(new Date(currentChallan?.data?.challan_date));
      }
    });
    challanDates.sort();
    const dateCheck = new Date(selectedDateInput);
    const dateFrom = new Date(challanDates[0]);
    const latestChallanDate = new Date(challanDates[challanDates.length - 1]);
    const dateTo = new Date(latestChallanDate.setFullYear(latestChallanDate.getFullYear() + 1));
    return dateCheck.getTime() <= dateTo.getTime() && dateCheck.getTime() >= dateFrom.getTime();
  }

  function checkSelectedDateGreaterLast(selectedDateInput) {
    const dateCheck = new Date(selectedDateInput);
    return dateCheck < new Date(lastDate.billing_date);
  }

  async function updatePrice(e) {
    const authData = JSON.parse(localStorage.getItem("AuthData"));
    const config = {
      headers: {
        Authorization: "Bearer " + authData.access_token,
      },
    };
    const tempPrice = await axios.post(`/api/v1/${authData?.type}/bill/getChallanPrice`, { challan_id: e.target.name }, config);
    if (e.target.checked) {
      setPrice(Math.round((price + Number(tempPrice.data.price)) * 100 / 100));
    } else {
      setPrice(Math.round((price - Number(tempPrice.data.price)) * 100 / 100));
    }
  }

  const handleCalculations = (transportCharges, discount) => {
    if (transportCharges && discount) {
      setPrice(Math.round((discount === 0 ? originalPrice + transportCharges : originalPrice * (1 - (discount / 100))) + transportCharges));
    } else if (transportCharges && !discount) {
      setPrice(Math.round(originalPrice + transportCharges));
    } else if (!transportCharges && discount) {
      setPrice(Math.round(discount === 0 ? originalPrice : originalPrice * (1 - (discount / 100))));
    } else {
      setPrice(Math.round(originalPrice));
    }
  };

  const getChallanPrice = async (id) => {
    try {
      const authData = JSON.parse(localStorage.getItem("AuthData"));
      const config = {
        headers: {
          Authorization: "Bearer " + authData.access_token,
        },
      };
      const priceResponse = await axios.post(`/api/v1/${authData?.type}/bill/getChallanPrice`, { challan_id: id }, config);
      return priceResponse?.data?.price || 0;
    } catch (error) {
      console.error("Error fetching price for challan:", error);
      return 0;
    }
  };

  useEffect(() => {
    getLastBillDate(details?.ap_company?._id);
  }, [details]);

  useEffect(() => {
    setupChallanTableRows();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchPrices = async () => {
      const prices = {};
      for (const row of allChallanTableRows) {
        const price = await getChallanPrice(row.id);
        prices[row.id] = price;
      }
      setChallanPrices(prices);
    };
    fetchPrices();
  }, [allChallanTableRows]);

  // Compute totals for display
  const baseTotal = parseFloat(price.toFixed(2));
  const totalWithTax = cash_payment
    ? baseTotal
    : roundOff
    ? Math.round(baseTotal * 1.18)
    : parseFloat((baseTotal * 1.18).toFixed(2));

  const handleChallanSelect = (e) => {
    const isChecked = e.target.checked;
    const challanId = e.target.name;
    if (isChecked && selectedCount() >= 10) {
      showErrorAlert("You can select a maximum of 10 challans only");
      return;
    }
    if (isChecked === false && selectedCount() === 1) {
      showErrorAlert("You have to select at least one challan");
      return;
    }
    setSelectedChallans({
      ...selectedChallans,
      [challanId]: isChecked,
    });
    updatePrice(e);
  };

  return (
    <>
      {(authenticatedUser?.role?.permissions.includes(`sync_bill_amount`) || userData?.type === "super_admin") &&
        details?.total_bill_cost_details?.paid === 0 && (
          <Grid item xs={12} sx={{ display: "none", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
            <LoadingButton variant="outlined" color="warning" startIcon={<SyncIcon />} onClick={handleBillSync} loading={billSyncing} loadingPosition="start">
              {billSyncing ? "Syncing" : "Sync Bill Amount"}
            </LoadingButton>
          </Grid>
        )}
      <Grid item xs={12}>
        <Formik
          initialValues={{
            name: details?.name,
            description: details?.description,
            billing_date: processDateFormat(new Date(details?.billing_date)),
            delivery: "",
            submit: false,
            discount_percent: details?.discount_percent || 0,
            transport_charges: details?.transport_charges || 0,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("Bill Name is required"),
            description: Yup.string().required("Description is required"),
            billing_date: Yup.string().required("Billing Date is required"),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting, resetForm }) => {
            try {
              submit(values, id);
            } catch (error) {
              const message = error.message || "Something went wrong";
              setStatus({ success: false });
              setErrors({ submit: message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            setFieldValue,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit} autoComplete="off">
              <Button ref={snackRef} style={{ display: "none" }} onClick={handleClick({ vertical: "top", horizontal: "center" })}>Click</Button>
              <Snackbar anchorOrigin={{ vertical, horizontal }} open={open} onClose={handleClose} key={vertical + horizontal}>
                {err ? <Alert severity="error">{err}</Alert> : success ? <Alert severity="success">{successMessage}</Alert> : null}
              </Snackbar>
              {!loader ? (
                <Card mb={6}>
                  <CardContent>
                    <Paper elevation={3} style={{ padding: "2%" }}>
                      <Grid container spacing={5}>
                        <Grid item xs={12} lg={12} md={12}>
                          <Typography variant="h6" gutterBottom>Update Bill</Typography>
                        </Grid>
                        <Grid item xs={12} md={12} lg={6}>
                          <TextField fullWidth name="bill_id" value={details?.bill_id} label="Bill ID" inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={12} lg={6}>
                          <TextField
                            fullWidth name="name" value={values.name} onBlur={handleBlur} onChange={handleChange}
                            error={Boolean(touched.name && errors.name)} label="Bill Name" helperText={touched.name && errors.name}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} lg={6}>
                          <TextField fullWidth name="ap_company" value={`${details?.ap_company?.name} [${details?.ap_company?.company_code}]`} label="AP Company" inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={12} lg={6}>
                          <TextField fullWidth name="customer_company" value={details?.customer_company?.name} label="Customer Company" inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={12} lg={12}>
                          <TextField
                            multiline fullWidth name="description" value={values.description} onBlur={handleBlur} onChange={handleChange}
                            error={Boolean(touched.description && errors.description)} label="Description" helperText={touched.description && errors.description}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} lg={6}>
                          <FormControl fullWidth error={Boolean(touched.delivery && errors.delivery)}>
                            <Autocomplete
                              options={details?.customer_company?.addresses}
                              onChange={(event, newValue) => setDeliveryAddress(newValue)}
                              value={deliveryAddress}
                              getOptionLabel={(option) => `${option?.address_line_one} ${option?.address_line_two} ${option?.city} ${option?.state} - Pincode: ${option?.pincode}`}
                              renderInput={(params) => (
                                <TextField {...params} error={Boolean(touched.delivery && errors.delivery)} helperText={touched.delivery && errors.delivery} label="Delivery Address" />
                              )}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={12} lg={6}>
                          <FormControl fullWidth error={Boolean(touched.customer_company && errors.customer_company)}>
                            <DateTimePicker
                              renderInput={(props) => <TextField {...props} error={touched.billing_date && errors.billing_date} />}
                              label="Billing Date"
                              value={values.billing_date || null}
                              onChange={(newValue) => {
                                if (!checkSelectedDateIsInBetween(newValue)) {
                                  showErrorAlert("Please select a date between the oldest challan date and one year from latest challan date");
                                  setFieldValue("billing_date", processDateFormat(new Date(details?.billing_date)));
                                  return;
                                }
                                if (checkSelectedDateGreaterLast(newValue)) {
                                  showErrorAlert(`Bill ${lastDate.bill_id} already created with date ${new Date(lastDate.billing_date).toLocaleDateString()} greater than selected date`);
                                  setFieldValue("billing_date", "");
                                  return;
                                }
                                setCurrentBillingDate(new Date(newValue));
                                let selectedDate = newValue;
                                var month = ("0" + parseInt(selectedDate?.getMonth() + 1)).slice(-2);
                                var day = ("0" + parseInt(selectedDate?.getDate())).slice(-2);
                                var hour = parseInt(selectedDate.getHours());
                                var minute = parseInt(selectedDate.getMinutes());
                                let date = `${selectedDate?.getFullYear()}-${month}-${day}:${hour}:${minute}`;
                                setFieldValue("billing_date", date);
                              }}
                              onBlur={handleBlur}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} lg={3} md={3}>
                          <FormControl component="fieldset" variant="standard">
                            <FormControlLabel control={<Checkbox checked={showNtb} onChange={() => setShowNtb(!showNtb)} />} label={"Show not to Bill Plates"} />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} lg={3} md={3}>
                          <FormControl component="fieldset" variant="standard">
                            <FormControlLabel control={<Checkbox checked={roundOff} onChange={() => setRoundOff(!roundOff)} />} label={"Round off amount"} />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                    <Divider />
                    <Paper elevation={3} style={{ padding: "2%" }}>
                      <Grid container spacing={5}>
                        <Grid item xs={12} lg={12} md={12}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h5" sx={{ mb: 3 }}>Challan Details (Max 10)</Typography>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                              <TextField value={searchChallan} onChange={(e) => setSearchChallan(e.target.value)} size="small" sx={{ margin: "0 8px" }} label="Search Challan" placeholder="Challan id" />
                              <Button endIcon={<SearchIcon />} variant='contained' sx={{ mr: 2 }} onClick={setupChallanTableRows}>Search</Button>
                            </Box>
                          </Box>
                          <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox"></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell>Chalan Id</TableCell>
                                  <TableCell>Chalan Name</TableCell>
                                  <TableCell>Chalan Date</TableCell>
                                  <TableCell>Chalan Amount</TableCell>
                                  <TableCell>Description</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {challanTableRows.map((row) => (
                                  <React.Fragment key={row.id}>
                                    <TableRow sx={{ backgroundColor: currentSelectedChallans.includes(row.id) ? "aliceblue" : "inherit" }}>
                                      <TableCell padding="checkbox">
                                        <Checkbox color="primary" checked={selectedChallans[row.id] || false} name={row.id} onClick={handleChallanSelect} />
                                      </TableCell>
                                      <TableCell>
                                        <IconButton size="small" onClick={() => setDown((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}>
                                          {down[row.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                        </IconButton>
                                      </TableCell>
                                      <TableCell>{row.challan_id}</TableCell>
                                      <TableCell>{row.name}</TableCell>
                                      <TableCell>{new Date(row.data.challan_date).toLocaleDateString()} {new Date(row.data.challan_date).toLocaleTimeString()}</TableCell>
                                      <TableCell>₹{challanPrices[row.id] !== undefined ? challanPrices[row.id] : "--"}</TableCell>
                                      <TableCell>{row.description}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                        <Collapse in={down[row.id]} timeout="auto" unmountOnExit>
                                          <Box sx={{ margin: 1 }}>
                                            <Typography variant="h6" gutterBottom>Challan Job Details</Typography>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>Job ID</TableCell>
                                                  <TableCell>Job Name</TableCell>
                                                  <TableCell align="center">Plate Quantity</TableCell>
                                                  <TableCell>Plate Size</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {row?.data?.jobs?.map((job, idx) => (
                                                  <TableRow key={idx}>
                                                    <TableCell>{job?.job_id}</TableCell>
                                                    <TableCell>{job?.name}</TableCell>
                                                    <TableCell align="center">{job?.plates.quantity}</TableCell>
                                                    <TableCell>
                                                      {job?.plates?.plate_details?.height}*{job?.plates?.plate_details?.width}*{job?.plates?.plate_details?.thickness}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                      <Box mt={13} px={6} mb={2} width={"100%"}>
                        <Typography variant="h5" gutterBottom>Bill Details</Typography>
                        <TableContainer component={Paper} sx={{ marginTop: "10px" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">Discount Percent</TableCell>
                                <TableCell align="center">Transport Charges</TableCell>
                                <TableCell align="center">HSN Code</TableCell>
                                <TableCell align="center">Total Amount</TableCell>
                                <TableCell align="center">Total Amount with Taxes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    value={discount_percent}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      setDiscountPercent(val);
                                      handleCalculations(transport_charges, val);
                                    }}
                                    type="number"
                                    InputProps={{ inputProps: { min: 0 } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    value={transport_charges}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      setTransportCharges(val);
                                      handleCalculations(val, discount_percent);
                                    }}
                                    type="number"
                                    InputProps={{ inputProps: { min: 0 } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField fullWidth value={hsnCode} onChange={(e) => setHsnCode(e.target.value)} placeholder="e.g. 84439959" />
                                </TableCell>
                                <TableCell>
                                  <TextField fullWidth value={baseTotal.toFixed(2)} InputProps={{ readOnly: true }} />
                                </TableCell>
                                <TableCell>
                                  <TextField fullWidth value={totalWithTax.toFixed(2)} InputProps={{ readOnly: true }} />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Paper>
                    <Divider />
                    <Paper elevation={3} style={{ padding: "2%" }}>
                      <Grid container spacing={5}>
                        <Grid item xs={12} md={12} lg={12}>
                          <Button type="submit" style={{ textAlign: "center", margin: "10px" }} variant="contained" color="primary" disabled={isSubmitting}>
                            Update Bill
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </CardContent>
                </Card>
              ) : (
                <Loading />
              )}
            </form>
          )}
        </Formik>
      </Grid>
    </>
  );
}

const getChallanList = async (customer_company, ap_company, setLoader, setChallanList, searchChallan) => {
  try {
    const authData = JSON.parse(localStorage.getItem("AuthData"));
    const config = {
      headers: {
        Authorization: "Bearer " + authData.access_token,
      },
    };
    const { data: responseData } = await axios.get(
      `/api/v1/${authData?.type}/challan?customer_company_id=${customer_company}&bill_created=NO&ap_company_id=${ap_company}&challan_uploaded=YES&populate_job=YES&populate_plate=YES&challan_search=${searchChallan}`,
      config
    );
    if (responseData.success) {
      setChallanList(responseData.data?.data);
    }
    if (!searchChallan) setLoader(false);
  } catch (e) {
    console.log(e);
    setLoader(false);
  }
};

const getBillDetails = async (id, setLoader, setDetails, setChallanList) => {
  try {
    const authData = JSON.parse(localStorage.getItem("AuthData"));
    const config = {
      headers: {
        Authorization: "Bearer " + authData.access_token,
      },
    };
    const { data: responseData } = await axios.get(`/api/v1/${authData?.type}/bill/${id}/bill_info`, config);
    if (responseData.success) {
      setDetails(responseData.data);
    }
    let searchChallan = "";
    getChallanList(
      responseData.data?.customer_company._id,
      responseData.data?.ap_company._id,
      setLoader,
      setChallanList,
      searchChallan
    );
  } catch (e) {
    console.log(e);
    setLoader(false);
  }
};

function UpdateBill({ authenticatedUser }) {
  const { bill_id } = useParams();
  const [loader, setLoader] = useState(true);
  const [details, setDetails] = useState({ data: [{}] });
  const [challanList, setChallanList] = useState([]);
  const [billId] = useState(bill_id);

  useEffect(() => {
    getBillDetails(bill_id, setLoader, setDetails, setChallanList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loader) {
    return (
      <React.Fragment>
        <Helmet title="Alpha Plus" />
        <Grid justifyContent="space-between" alignItems="center" container spacing={10} mb={4}>
          <Grid item>
            <Typography variant="h4" gutterBottom display="inline" sx={{ fontWeight: 600 }} color="primary">UPDATE BILL</Typography>
          </Grid>
          <Grid item>
            <Breadcrumbs aria-label="Breadcrumb" mt={2}>
              <Link component={NavLink} to="/dashboard/tasks">Dashboard</Link>
              <Link component={NavLink} to="/dashboard/bill">All Bills</Link>
              <Typography>Update Bill</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <FormDetails
            id={billId}
            details={details}
            challanList={challanList}
            setChallanList={setChallanList}
            authenticatedUser={authenticatedUser}
            setLoader={setLoader}
          />
        </Grid>
      </React.Fragment>
    );
  } else {
    return <Loading />;
  }
}

const mapStateToProps = (state) => ({
  authenticatedUser: state.auths?.user,
});

export default connect(mapStateToProps, null)(UpdateBill);